export interface AuthErrorData {
  type?: string;
  field?: string;
  message?: string;
  code?: string | number;
  severity?: string;
}

export interface AuthErrorResult {
  error: string | null;
  fieldErrors: Record<string, string>;
  reason?: AuthErrorReason;
}

export type AuthErrorReason =
  | 'email-not-verified'
  | 'invalid-credentials'
  | 'invalid-password';

export interface AuthErrorLocalizedCopy {
  emailNotVerified: string;
  invalidCredentials: string;
  invalidPassword: string;
  invalidVerificationCode: string;
  verificationCodeExpired: string;
  accountDeletionPending?: string;
}

/**
 * Common error types for authentication flows.
 */
export const AUTH_ERROR_TYPES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  RATE_LIMIT: 'RATE_LIMIT',
  ACCOUNT_EXISTS: 'ACCOUNT_EXISTS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  INVALID_VERIFICATION_CODE: 'INVALID_VERIFICATION_CODE',
  VERIFICATION_CODE_EXPIRED: 'VERIFICATION_CODE_EXPIRED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  // As per Firebase conventions mentioned in GH issue
  // https://github.com/get-convex/convex-auth/issues/124
  WRONG_PASSWORD: 'auth/wrong-password',
  INVALID_EMAIL: 'auth/invalid-email',
  USER_NOT_FOUND: 'auth/user-not-found',
  EMAIL_EXISTS: 'auth/email-already-in-use',
  WEAK_PASSWORD: 'auth/weak-password',
} as const;

export type AuthErrorType =
  (typeof AUTH_ERROR_TYPES)[keyof typeof AUTH_ERROR_TYPES];

/** Sign-in refused while the account's deletion is being completed. */
export const ACCOUNT_DELETION_PENDING_CODE = 'ACCOUNT_DELETION_PENDING';
const ACCOUNT_DELETION_PENDING_FALLBACK =
  'Your account deletion is still being completed. Contact support if this takes more than two days.';

/** The calm message for a refused sign-in during account deletion. */
export function accountDeletionPendingResult(
  localizedCopy?: AuthErrorLocalizedCopy
): AuthErrorResult {
  return {
    error:
      localizedCopy?.accountDeletionPending ??
      ACCOUNT_DELETION_PENDING_FALLBACK,
    fieldErrors: {},
  };
}
